import { useCallback, type ReactNode } from 'react';

import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The CANONICAL CATALOGUE.
 *
 * The platform's canonical products — the reference catalogue a tenant creates its own offers
 * from. It is not the tenant's catalogue: the tenant's sellable offers are a separate read,
 * shown by the offers widget.
 *
 * M66's catalogue READ is public scope, so this widget has no permission to gate on — it is
 * rendered for every signed-in actor. Its WRITES are platform-scoped, and nothing here writes.
 */
const STATE_LABEL: Readonly<Record<string, string>> = {
  draft: 'Draft',
  published: 'Published',
  retired: 'Retired',
  merged: 'Merged',
};

export function CatalogueSummaryWidget({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.catalogue(), []);

  return (
    <DashboardWidgetFrame
      title="Canonical catalogue"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No canonical products"
      emptyDescription="The platform's reference catalogue is empty."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <StatCard
              label="Products"
              value={String(data.total)}
              caption="In the canonical catalogue"
            />
            <StatCard
              label="Published"
              value={String(data.published)}
              tone={data.published === 0 ? 'warning' : 'positive'}
            />
          </div>

          <ul className="flex flex-col">
            {Object.entries(data.byState).map(([state, count]) => (
              <li
                key={state}
                className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
              >
                <span className="text-content">{STATE_LABEL[state] ?? state}</span>
                <span className="font-medium text-content">{count}</span>
              </li>
            ))}
          </ul>

          {/* The products are NAMED. A catalogue summary that reports how many products
              there are, and not which ones, gives the reader nothing to act on. */}
          {data.products.length > 0 ? (
            <ul className="flex flex-col">
              {data.products.map((product) => (
                <li
                  key={product.id}
                  className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
                >
                  <span className="min-w-0 truncate text-content">
                    {product.name ?? product.modelCode ?? product.id}
                  </span>
                  <span className="shrink-0 font-medium text-content-muted">
                    {STATE_LABEL[product.state] ?? product.state}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
